import { Link } from "react-router-dom";
import { useProtocolHealth } from "../hooks/useProtocolHealth.js";

/**
 * Sticky banner for the two conditions that stop normal service. They are
 * NOT the same thing and must not share copy:
 *
 *   depositsPaused — DePegGuard tripped (§13) or governance disabled new
 *     deposits (§3.1). Only NEW deposits are blocked; existing positions,
 *     withdrawals, lending and treasury execution stay live.
 *
 *   protocolFrozen — the §Emergency Brake is engaged. This one DOES stop
 *     withdrawals, settlements and exits, so the deposits-paused copy
 *     ("withdrawals remain available") would be actively false here. It
 *     gets its own, franker variant instead.
 *
 * Either way we spell out what is and isn't affected in the body, to head
 * off the "is my money safe?" support question.
 *
 * Interest-reserve breaches are NOT surfaced in this banner. Per client
 * feedback: there is no action the end-user can take on a coverage
 * breach (Marketing/Ops extraction halt is the protocol's own §11.7
 * response, governance-level decision), so a red sticky banner just
 * panics readers without giving them a path. The same status lives on
 * the Protocol Health page where it sits next to reserve-bucket charts,
 * coverage curves, and the operator runbook — the right surface for
 * the audience that actually acts on it.
 *
 * The banner reads from the cached `useProtocolHealth` snapshot so
 * every page sharing the hook gets the same value without re-fetching.
 */
export default function PausedDepositsBanner() {
    const { data } = useProtocolHealth();

    if (!data) return null;

    const frozen = Boolean(data.protocolFrozen);
    const paused = Boolean(data.depositsPaused);

    if (!frozen && !paused) return null;

    // The freeze is the stronger condition and subsumes the other, so it wins.
    return (
        <div className="flex flex-col">
            {frozen ? <FrozenBanner /> : <PausedBanner />}
        </div>
    );
}

function PausedBanner() {
    return (
        <Banner
            title="New Term Deposits are paused"
            tone="bg-[#FBC206] text-[#10281b] border-[#10281b]/15"
            pill="bg-[#10281b] text-[#FBC206] hover:bg-[#0E2C19]"
        >
            New Term Deposits are temporarily paused by a protocol safety
            control. Existing positions keep accruing; withdrawals, lending and
            repayments remain available. See Protocol Health for the current
            reason.
        </Banner>
    );
}

function FrozenBanner() {
    return (
        <Banner
            title="The protocol is temporarily frozen"
            tone="bg-[#B3261E] text-white border-white/20"
            pill="bg-white text-[#B3261E] hover:bg-white/90"
        >
            The emergency brake is engaged while an incident is investigated.
            Deposits, withdrawals and exits are all paused for now. Nothing has
            been moved or taken — your balance is unchanged and interest keeps
            accruing. Lifting the freeze requires confirmation from every
            multisig signer.
        </Banner>
    );
}

function Banner({ title, tone, pill, children }) {
    return (
        <div role="alert" aria-live="polite" className={`border-b ${tone}`}>
            <div className="mx-auto flex max-w-7xl items-start gap-3 px-4 py-2.5 sm:px-8">
                <PauseIcon />
                <div className="flex-1 min-w-0">
                    <div className="font-manrope text-[12.5px] font-bold">{title}</div>
                    <p className="mt-0.5 text-[11px] leading-snug opacity-90">{children}</p>
                </div>
                <Link
                    to="/protocol-health"
                    className={`hidden sm:inline-flex shrink-0 items-center rounded-full px-3 py-1 text-[10.5px] font-semibold ${pill}`}
                >
                    Protocol Health
                </Link>
            </div>
        </div>
    );
}

function PauseIcon() {
    return (
        <svg
            className="mt-0.5 h-5 w-5 shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <circle cx="12" cy="12" r="10" />
            <line x1="10" y1="9" x2="10" y2="15" />
            <line x1="14" y1="9" x2="14" y2="15" />
        </svg>
    );
}

